import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Alert } from 'react-bootstrap';
import { usePrinterStore } from 'stores/printerStore';
import {
  requestBluetoothDevice,
  requestUsbDevice,
  printReceipt,
  type SavedPrinter,
  type ReceiptData,
} from 'utils/printerUtils';
import { ROUTES } from 'utils/constants';
import Button from 'components/Button';
import PageTitle from 'components/PageTitle';
import { Printer, Bluetooth, Usb, Trash2, Info, ArrowLeft } from 'lucide-react';
import styles from './PrinterSettings.module.scss';

const PrinterSettings: React.FC = () => {
  const navigate = useNavigate();

  const printerSettings = usePrinterStore((s) => s.printerSettings);
  const updatePrinterSettings = usePrinterStore((s) => s.updatePrinterSettings);
  const savedPrinter = usePrinterStore((s) => s.savedPrinter);
  const setSavedPrinter = usePrinterStore((s) => s.setSavedPrinter);
  const activeDevice = usePrinterStore((s) => s.activeDevice);
  const setActiveDevice = usePrinterStore((s) => s.setActiveDevice);

  const [isPrinting, setIsTestPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBack = () => {
    navigate(ROUTES.POS.ROOT);
  };

  const handleConnectBluetooth = async () => {
    setError(null);
    try {
      const device = await requestBluetoothDevice();
      if (device) {
        const newPrinter: SavedPrinter = {
          type: 'bluetooth',
          id: device.id,
          name: device.name || 'Bluetooth Printer',
        };
        void setSavedPrinter(newPrinter);
        setActiveDevice(device);
      }
    } catch (err) {
      if ((err as Error).name !== 'NotFoundError') {
        setError((err as Error).message);
      }
    }
  };

  const handleConnectUsb = async () => {
    setError(null);
    try {
      const device = await requestUsbDevice();
      if (device) {
        const newPrinter: SavedPrinter = {
          type: 'usb',
          id: `${device.vendorId}_${device.productId}`,
          name: device.productName || 'USB Printer',
        };
        void setSavedPrinter(newPrinter);
        setActiveDevice(device);
      }
    } catch (err) {
      if ((err as Error).name !== 'NotFoundError') {
        setError((err as Error).message);
      }
    }
  };

  const handleForgetPrinter = () => {
    void setSavedPrinter(null);
    setActiveDevice(null);
  };

  const handleTestPrint = async () => {
    if (!savedPrinter) return;

    setIsTestPrinting(true);
    setError(null);
    try {
      const testData: ReceiptData = {
        items: [],
        total: 0,
        paymentMethod: 'Test',
        amountReceived: 0,
        change: 0,
        shouldCut: printerSettings.autoCut,
      };

      const newDevice = await printReceipt(
        testData,
        savedPrinter,
        activeDevice,
      );
      if (newDevice) setActiveDevice(newDevice);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsTestPrinting(false);
    }
  };

  const isConnected =
    (activeDevice as BluetoothDevice | null)?.gatt?.connected ||
    (activeDevice as USBDevice | null)?.opened;

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <button
          className={styles.backBtn}
          onClick={handleBack}
          title="Back to POS"
        >
          <ArrowLeft size={24} />
        </button>
        <PageTitle />
        <h1>Printer Settings</h1>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <div className={styles.section}>
        <h2>General Settings</h2>
        <div className={styles.settingsGrid}>
          <Form.Group className={styles.formGroup}>
            <Form.Check
              type="switch"
              id="enabled"
              label="Enable Printing"
              checked={printerSettings.enabled}
              onChange={(e) =>
                updatePrinterSettings({ enabled: e.target.checked })
              }
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Check
              type="switch"
              id="autoCut"
              label="Paper Cutter (Auto-cut)"
              checked={printerSettings.autoCut}
              onChange={(e) =>
                updatePrinterSettings({ autoCut: e.target.checked })
              }
            />
          </Form.Group>
        </div>
      </div>

      <div className={styles.section}>
        <h2>Printer Connection</h2>
        {savedPrinter ? (
          <div className={styles.selectedPrinter}>
            <div className={styles.printerInfo}>
              <div className={styles.printerIcon}>
                {savedPrinter.type === 'bluetooth' ? (
                  <Bluetooth size={24} />
                ) : (
                  <Usb size={24} />
                )}
              </div>
              <div className={styles.printerDetails}>
                <h3>{savedPrinter.name}</h3>
                <p>
                  Type: {savedPrinter.type.toUpperCase()} | Status:{' '}
                  <span
                    className={
                      isConnected ? styles.statusOnline : styles.statusOffline
                    }
                  >
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </span>
                </p>
              </div>
            </div>
            <div className={styles.printerActions}>
              <Button
                variant="outline"
                onClick={() => {
                  void handleTestPrint();
                }}
                disabled={isPrinting}
              >
                <Printer size={18} />
                {isPrinting ? 'Printing...' : 'Test Print'}
              </Button>
              <Button
                variant="outline"
                className={styles.forgetBtn}
                onClick={handleForgetPrinter}
              >
                <Trash2 size={18} />
                Forget
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.connectButtons}>
            <Button
              variant="outline"
              onClick={() => {
                void handleConnectBluetooth();
              }}
              className={styles.connectBtn}
            >
              <Bluetooth size={20} />
              Connect Bluetooth Printer
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                void handleConnectUsb();
              }}
              className={styles.connectBtn}
            >
              <Usb size={20} />
              Connect USB Printer
            </Button>
          </div>
        )}
      </div>

      <div className={styles.helpSection}>
        <Info size={20} />
        <div>
          <h4>Connection Help</h4>
          <p>
            Bluetooth printers must be in pairing mode. USB printers must be
            plugged in and supported by your browser. Ensure the printer is
            switched on.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrinterSettings;
